import { useEffect } from 'react';

import { buildNavPath } from 'helpers/candidate_application/form-page-helpers';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Hook to handle redirects when a page becomes invalid due to visibility changes.
 * This is useful for multi-page sections where subsequent subsections may have
 * conditional rendering based on values from previous subsections.
 */
const usePageVisibilityRedirect = ({
  currentPage,
  totalPages,
  visibleSubsections,
  preSubsectionFlows = [],
  hasIndexPage = false,
  sectionKey,
  token,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const admin = searchParams.get('admin');

  useEffect(() => {
    // Skip if we're on the index page (page 0 with hasIndexPage)
    if (hasIndexPage && currentPage === 0) {
      return;
    }

    // Calculate the adjusted page index (accounting for index page)
    const adjustedPageIndex = hasIndexPage ? currentPage - 1 : currentPage;

    // Check if we're trying to access a subsection that no longer exists
    const subsectionIndex = adjustedPageIndex - preSubsectionFlows.length;
    if (subsectionIndex >= 0 && subsectionIndex >= visibleSubsections.length) {
      // Current page is out of bounds - redirect to the last valid page or dashboard
      const lastValidPage =
        (hasIndexPage ? 1 : 0) + preSubsectionFlows.length + visibleSubsections.length - 1;
      if (lastValidPage >= (hasIndexPage ? 1 : 0)) {
        navigate(
          buildNavPath(
            `/form_submission/candidate/${sectionKey}/${token}/page/${lastValidPage}`,
            admin,
          ),
          {
            replace: true,
          },
        );
      } else {
        navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`, admin), {
          replace: true,
        });
      }
    }
  }, [
    currentPage,
    totalPages,
    visibleSubsections.length,
    preSubsectionFlows.length,
    hasIndexPage,
    sectionKey,
    token,
    navigate,
    admin,
  ]);
};

export default usePageVisibilityRedirect;
